import React, { useState, useEffect, useCallback } from "react";
import { Modal, Table, Breadcrumb, Button, Space, Empty, Spin, Typography, Image } from "antd";
import {
  FolderOutlined,
  FileOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  CodeOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  HomeOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";

const { Text } = Typography;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

// Get icon based on file type
const getFileIcon = (type, size = 24) => {
  const iconProps = { style: { fontSize: size } };
  
  switch (type) {
    case 'folder':
      return <FolderOutlined {...iconProps} style={{ ...iconProps.style, color: '#1890ff' }} />;
    case 'image':
      return <FileImageOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
    case 'video':
      return <VideoCameraOutlined {...iconProps} style={{ ...iconProps.style, color: '#722ed1' }} />;
    case 'pdf':
      return <FilePdfOutlined {...iconProps} style={{ ...iconProps.style, color: '#f5222d' }} />;
    case 'document':
      return <FileTextOutlined {...iconProps} style={{ ...iconProps.style, color: '#fa8c16' }} />;
    case 'code':
      return <CodeOutlined {...iconProps} style={{ ...iconProps.style, color: '#13c2c2' }} />;
    case 'archive':
      return <FileZipOutlined {...iconProps} style={{ ...iconProps.style, color: '#faad14' }} />;
    case 'audio':
      return <AudioOutlined {...iconProps} style={{ ...iconProps.style, color: '#eb2f96' }} />;
    default:
      return <FileOutlined {...iconProps} />;
  }
};

const CDNFilePicker = ({ visible, onSelect, onCancel, basePath = '', fileTypes = [] }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [currentPath, setCurrentPath] = useState(basePath);
  const [continuationToken, setContinuationToken] = useState(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();

  // Fetch items from the API
  const fetchItems = useCallback(async (path, token = null) => {
    if (!token) {
      setLoading(true);
      setItems([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const accessToken = await getTokenSilently();
      const params = new URLSearchParams({
        prefix: path,
      });
      
      if (token) {
        params.append('continuationToken', token);
      }

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/cdn-files?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        let newItems = data.data.items || [];
        
        // Filter by file types if specified
        if (fileTypes.length > 0) {
          newItems = newItems.filter(item => {
            if (item.type === 'folder') return true;
            return fileTypes.includes(item.type);
          });
        }
        
        if (!token) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }
        
        setBreadcrumbs(data.data.breadcrumbs || []);
        setIsTruncated(data.data.isTruncated || false);
        setContinuationToken(data.data.nextContinuationToken || null);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch CDN files:", response.status, errorText);
      }
    } catch (error) {
      console.error("Error fetching CDN files:", error);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [getTokenSilently, isProd, fileTypes]);

  // Load items when modal opens or path changes
  useEffect(() => {
    if (visible) {
      fetchItems(currentPath);
    }
  }, [visible, currentPath, fetchItems]);

  // Navigate to a folder
  const navigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  // Handle preview
  const handlePreview = (file) => {
    if (file.type === 'image') {
      const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
      const fileUrl = `${cdnBaseUrl}/${file.path}`;
      setPreviewImage(fileUrl);
      setPreviewVisible(true);
    }
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
    const fileUrl = `${cdnBaseUrl}/${file.path}`;
    onSelect(fileUrl);
  };

  // Load more items
  const handleLoadMore = () => {
    if (continuationToken && !loadingMore) {
      fetchItems(currentPath, continuationToken);
    }
  };

  // Refresh the current view
  const handleRefresh = () => {
    fetchItems(currentPath);
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
        const fileUrl = `${cdnBaseUrl}/${record.path}`;
        
        return (
          <Space>
            {record.type === 'image' ? (
              <div style={{
                width: 40,
                height: 40,
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                overflow: 'hidden',
                background: 'repeating-conic-gradient(#f0f0f0 0% 25%, transparent 0% 50%) 50% / 10px 10px',
                display: 'inline-block',
              }}>
                <Image
                  width={40}
                  height={40}
                  src={fileUrl}
                  preview={false}
                  style={{ 
                    objectFit: 'cover', 
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={() => handlePreview(record)}
                />
              </div>
            ) : (
              getFileIcon(record.type)
            )}
            <Text>{text}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size, record) => record.type === 'folder' ? '-' : formatFileSize(size),
    },
    {
      title: 'Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      width: 200,
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        if (record.type === 'image') {
          return (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handlePreview(record);
              }}
            >
              Preview
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const rowSelection = {
    type: 'radio',
    onSelect: (record) => {
      if (record.type !== 'folder') {
        handleFileSelect(record);
      }
    },
  };

  return (
    <>
      <Modal
        title="Select File from CDN"
        visible={visible}
        onCancel={onCancel}
        width={800}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
        ]}
      >
      <div style={{ marginBottom: "16px" }}>
        <Breadcrumb>
          <Breadcrumb.Item>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={() => navigateToFolder(basePath)}>
              <HomeOutlined /> CDN
            </a>
          </Breadcrumb.Item>
          {breadcrumbs.map((crumb, index) => (
            <Breadcrumb.Item key={index}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a onClick={() => navigateToFolder(crumb.path)}>
                {crumb.name}
              </a>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>

      <div style={{ marginBottom: "16px", textAlign: "right" }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          description="No files found"
          style={{ padding: "50px" }}
        />
      ) : (
        <>
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(record) => record.path}
            pagination={false}
            size="small"
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: () => {
                if (record.type === 'folder') {
                  navigateToFolder(record.path);
                } else {
                  handleFileSelect(record);
                }
              },
              style: { cursor: 'pointer' },
            })}
          />
          
          {isTruncated && (
            <div style={{ textAlign: "center", marginTop: "16px" }}>
              <Button
                loading={loadingMore}
                onClick={handleLoadMore}
                size="small"
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
      </Modal>
      
      <Modal
        visible={previewVisible}
        title="Image Preview"
        footer={[
          <Button key="select" type="primary" onClick={() => {
            handleFileSelect({ path: previewImage.replace(`${config.cdn[isProd ? "prod" : "dev"]}/`, '') });
            setPreviewVisible(false);
          }}>
            Select This Image
          </Button>,
          <Button key="cancel" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>
        ]}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        zIndex={2000}
        maskStyle={{ zIndex: 1999 }}
      >
        <div style={{ 
          textAlign: 'center',
          padding: '20px',
          background: 'repeating-conic-gradient(#f9f9f9 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
          borderRadius: '4px',
        }}>
          <Image
            src={previewImage}
            style={{ maxWidth: '100%' }}
            preview={{
              visible: false,
              mask: null,
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default CDNFilePicker;